import FormErrors from "@/components/form/FormErrors";
import * as c from "@/components/ui";
import ShockwaveDetailsFormFields from "@/features/shockwave/components/ShockwaveDetailsFormFields";
import { useTemplateNavigationStore } from "@/features/templates/hooks/useTemplateNavigationStore";
import { useTemplateStore } from "@/features/templates/stores/useTemplateStore";
import { services as s } from "@/services";
import { templateSettingsSchema } from "@/types/zod/quickstart-settings";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import type { z } from "zod";

interface RouteParams {
	shockwaveDetailId: string;
	templateId: string;
}

type ShockwaveDetailsValues = Pick<
	z.infer<typeof templateSettingsSchema>,
	"shockwave_details"
>;

const ShockwaveDetailsFormFieldsPage = () => {
	const { templateId } = Route.useParams();
	const { shockwave_id, shockwave_details, setShockwaveDetails } =
		useTemplateStore();
	const {
		markStepComplete,
		setCurrentStep,
		getNextStepRoute,
		getPreviousStepRoute,
		currentStep,
	} = useTemplateNavigationStore();
	const navigate = useNavigate();
	const form = useForm<ShockwaveDetailsValues>({
		resolver: zodResolver(
			templateSettingsSchema.pick({
				shockwave_details: true,
			}),
		),
		defaultValues: {
			shockwave_details: shockwave_details ?? [
				{
					shockwave_id: shockwave_id ?? "",
					shockwave_detail_id: s.uuidv4(),
					side: "",
					orthopaedic_area: "",
					site: "",
					subsite: "",
					shockwave_type: "",
					dose: "",
					unit: "",
					impulses: "",
				},
			],
		},
	});

	useEffect(() => {
		setCurrentStep("shockwave-details");
	}, [setCurrentStep]);

	const onSubmit = async (data: ShockwaveDetailsValues) => {
		try {
			setShockwaveDetails(data);
			markStepComplete(currentStep);
			const nextRoute = getNextStepRoute(currentStep, { templateId });
			if (nextRoute) navigate(nextRoute);
		} catch (error) {
			toast.error("Failed to save shockwave details");
			console.error("Form submission error:", error);
		}
	};

	const handlePrevious = () => {
		const prevRoute = getPreviousStepRoute(currentStep, { templateId });
		if (prevRoute) navigate(prevRoute);
	};

	return (
		<>
			<div className="mb-4">
				<h2 className="mb-2 font-semibold text-xl">
					Shockwave Details
				</h2>
				<p className="text-slate-500 dark:text-slate-300">
					Add details about each shockwave treatment site
				</p>
			</div>
			<c.ui.Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit)}
					className="w-full form"
				>
					<ShockwaveDetailsFormFields
						shockwaveId={shockwave_id ?? ""}
					/>

					<FormErrors />

					{/* Navigation Buttons */}
					<div className="flex justify-between gap-4 mt-8">
						<c.ui.Button
							type="button"
							variant="outline"
							onClick={handlePrevious}
						>
							Previous
						</c.ui.Button>

						<c.ui.Button type="submit">Next</c.ui.Button>
					</div>
				</form>
			</c.ui.Form>
		</>
	);
};

export const Route = createFileRoute(
	"/_app/new/diary/$templateId/events/shockwave/details",
)({
	component: ShockwaveDetailsFormFieldsPage,
	loader: ({ params }: { params: RouteParams }) => {
		return {
			shockwaveDetailId: params.shockwaveDetailId,
		};
	},
});
